import type { IconName } from '../components/Icon';
import type { AuditEvent } from '../types';

// Action labels describe the recorded operation, never infer its business result.
const actions: Record<string, string> = {
  'work.proposed': 'Work proposed', 'work.review_started': 'Work review',
  'work.approved': 'Work approval', 'work.rejected': 'Work decision',
  'work.started': 'Work execution', 'work.succeeded': 'Work execution',
  'work.failed': 'Work execution', 'work.blocked': 'Work decision', 'work.cycle': 'Work cycle',
  'outbox.stage': 'Content staged', 'outbox.edit': 'Content edit', 'outbox.approve': 'Content approval',
  'outbox.agent_review': 'Content review', 'outbox.publish': 'Publication', 'outbox.publish_hero': 'Article image update',
  'agent_run.submit': 'Agent run request', 'agent_run.complete': 'Agent run result',
  'metric_collection.submit': 'Metrics collection request', 'metric_collection.complete': 'Metrics collection result',
  'prospect_research.submit': 'Company research request', 'prospect_research.complete': 'Company research result',
  'prospect.review': 'Prospect review', 'prospect.outreach_review': 'Outreach review',
  'prospect.preview_requested': 'Preview request', 'prospect.preview_completed': 'Preview result',
  'prospect.preview_reopened': 'Preview review reopened', 'prospect.contact_supplied': 'Contact update',
  'prospect.contact_researched': 'Contact research', 'prospect_outreach.pause': 'Outreach pause setting',
  'prospect_outreach.cycle': 'Outreach cycle', 'apollo_enrichment.submit': 'Apollo lookup request',
  'apollo_enrichment.complete': 'Apollo lookup result', 'apollo_contact_review.submit': 'Contact review request',
  'apollo_contact_review.complete': 'Contact review result', 'apollo_contact.review_claimed': 'Contact review started',
  'apollo_contact.review_completed': 'Contact review result', 'apollo.credential.removed': 'Apollo credential removal',
  'mail.cycle': 'Mailbox cycle', 'backlog.create': 'Backlog item creation', 'backlog.update': 'Backlog update',
  'experiment.create': 'Experiment creation', 'experiment.transition': 'Experiment decision',
  'commercial_snapshot.create': 'Commercial evidence recorded', 'article_image.creative_revision': 'Article image review'
};
const objects: Record<string, string> = {
  work: 'Work', outbox: 'Content', backlog: 'Backlog', agent_run: 'Agent run', management_job: 'Job',
  mail: 'Mailbox', prospect: 'Prospect', prospect_outreach: 'Outreach', experiment: 'Experiment',
  campaign: 'Campaign', crm_record: 'Relationship'
};
export function auditAction(action: string): string { return actions[action] || 'Recorded action'; }
export function auditObject(item: AuditEvent): string {
  const label = objects[item.objectType] || 'Object';
  return /^[1-9]\d*$/.test(item.objectId) && Number.isSafeInteger(Number(item.objectId)) ? `${label} #${item.objectId}` : label;
}
export function auditObjectLink(item: AuditEvent, manageProspects: boolean): string | null {
  if (!/^[1-9]\d*$/.test(item.objectId) || !Number.isSafeInteger(Number(item.objectId))) return null;
  if (item.objectType === 'work') return '/work?status=&item=' + item.objectId;
  if (item.objectType === 'outbox') return '/publishing?item=' + item.objectId;
  if (item.objectType === 'prospect' && manageProspects) return '/relationships?item=' + item.objectId;
  // Jobs are not run IDs; mail resources are not necessarily thread IDs.
  // Do not fabricate deep links for pages without an object reader.
  return null;
}
export function auditOutcome(outcome: string): { label: string; tone: 'complete' | 'attention' | 'neutral'; icon: IconName } {
  if (outcome === 'SUCCEEDED') return { label: 'Completed', tone: 'complete', icon: 'check' };
  if (['FAILED', 'REJECTED', 'BLOCKED'].includes(outcome)) return {
    label: { FAILED: 'Failed', REJECTED: 'Rejected', BLOCKED: 'Blocked' }[outcome]!, tone: 'attention', icon: 'warning'
  };
  return { label: ({ APPROVED: 'Approved', ACCEPTED: 'Accepted', PROPOSED: 'Proposed', REVIEW_STARTED: 'Review started',
    STARTED: 'Started', RUNNING: 'Running', PENDING: 'Pending' } as Record<string, string>)[outcome] || 'Recorded', tone: 'neutral', icon: 'clock' };
}
function auditTimestamp(value: string): Date | null {
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/.test(value)) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}
export function auditTime(value: string): string {
  const date = auditTimestamp(value);
  return date ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(date) : 'Unknown';
}
export function auditGroups(items: AuditEvent[]): { key: string; label: string; items: AuditEvent[] }[] {
  const groups = new Map<string, { item: AuditEvent; time: number; index: number }[]>();
  items.forEach((item, index) => {
    const date = auditTimestamp(item.occurredAt), key = date?.toISOString().slice(0, 10) || '';
    const rows = groups.get(key) || [];
    rows.push({ item, time: date?.getTime() || 0, index }); groups.set(key, rows);
  });
  return [...groups.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([key, rows]) => ({
    key: key || 'unavailable', label: key ? new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
    }).format(new Date(key + 'T00:00:00Z')) : 'Timestamp unavailable',
    items: rows.sort((a, b) => b.time - a.time || a.index - b.index).map(row => row.item)
  }));
}
export function auditPageCounts(items: AuditEvent[]) {
  return items.reduce((counts, item) => {
    counts[auditOutcome(item.outcome).tone]++; return counts;
  }, { complete: 0, attention: 0, neutral: 0 });
}
